// Picks a transport: WebTransport when both the browser and the server support it, otherwise WebSocket.
// The game only ever sees the Transport interface from shared/transport.ts.
import type { Transport, TransportKind } from '../../../shared/transport.js';
import type { WireData } from '../../../shared/codec.js';

export function websocketTransport(url: string): Promise<Transport> {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    const t: Transport = {
      kind: 'websocket',
      get backlog() { return ws.bufferedAmount; },
      sendReliable(d: WireData) { if (ws.readyState === WebSocket.OPEN) ws.send(d); },
      // One ordered stream underneath, so "unreliable" just means: skip it if the socket is backed up.
      sendUnreliable(d: WireData) { if (ws.readyState === WebSocket.OPEN && ws.bufferedAmount < 16384) ws.send(d); },
      close(code = 1000, reason = '') { ws.close(code, reason); },
      onMessage: () => {},
      onClose: () => {},
    };
    ws.onopen = () => resolve(t);
    ws.onerror = () => reject(new Error('websocket failed'));
    ws.onmessage = e => t.onMessage(typeof e.data === 'string' ? e.data : new Uint8Array(e.data as ArrayBuffer));
    ws.onclose = e => t.onClose(`closed ${e.code}${e.reason ? ' ' + e.reason : ''}`);
  });
}

/** True when this browser has the WebTransport API (all major browsers since March 2026). */
export const browserHasWebTransport = (): boolean => typeof (globalThis as { WebTransport?: unknown }).WebTransport === 'function';

/**
 * WebTransport goes here once the server speaks HTTP/3: reliable messages on one bidirectional stream with a
 * 4-byte length prefix per frame, unreliable messages as datagrams. Until then the server advertises no
 * WebTransport URL and this is never called.
 */
export async function webtransportTransport(_url: string): Promise<Transport> {
  throw new Error('WebTransport is not enabled on this server yet');
}

export async function connectBest(wsUrl: string, wtUrl: string | null): Promise<{ t: Transport; note: string }> {
  if (wtUrl && browserHasWebTransport()) {
    try { return { t: await webtransportTransport(wtUrl), note: '' }; }
    catch (e) { return { t: await websocketTransport(wsUrl), note: 'WebTransport failed, using WebSocket' }; }
  }
  const t = await websocketTransport(wsUrl);
  return { t, note: browserHasWebTransport() ? 'browser supports WebTransport; server not yet' : 'no WebTransport in this browser' };
}

export type { TransportKind };
